import { FC, Suspense, useCallback, useEffect } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { PageContextWrapper } from "@ryde/controls";
import { useSelectedIds, useYupValidationResolver } from "@ryde/redux";
import {
  activeStationsListSelector,
  BOOKING_BULK_FORM_FIELDS,
  bookingBulkCreateStateFromPollingState,
  bookingCalendarBulkSetPreviewSuccessfullyBookedSelector,
  bulkApprovalBookingConflictsUpdatedAtom,
  DirectionEnum,
  dropdownAtomFamily,
  getDirectionAndBookingIds,
  IBulkCreateBookingsProps,
  IBulkSetBookingsDto,
  IBulkSetBookingsForm,
  IBulkSetBookingsPassengerDto,
  IDropdownItemShifts,
  IInjectedDrawerProps,
  isBulkBookingSetAbortedSelector,
  isBulkBookingSetFinishSelector,
  lastChosenBulkCreateBookingDirectionAtom,
  parseBookingCellId,
  selectedCellsBookingCalendarAtom,
  selectedCellsBookingsSelector,
  systemSettingsDtoAtom,
  useDrawers,
} from "@ryde/shared";

import { CommonDefaultDrawer } from "../../common-default-drawer";
import { BaseDrawer } from "../../drawer";
import {
  BulkCreateBookingFooter,
  bulkCreateBookingsValidationSchema,
  getApprovalBookingConflictAPI,
  getBulkBookingDirectionMultiplier,
  isMealOnlyShiftSelected,
  IUseBulkCreateGetPayloadParams,
  PreviewStepForm,
  useBulkCreateBooking,
} from "../bulk-bookings-components";
import { BulkCreateBookingsForm } from "./bulk-create-booking-form";

export const BulkCreateBookingsSuspended: FC<
  IBulkCreateBookingsProps & IInjectedDrawerProps
> = ({ onCloseDrawer, drawerNumber }) => {
  const { t } = useTranslation();
  const activeStationsList = useRecoilValue(activeStationsListSelector);
  const { openBookingProduction } = useDrawers();
  const { clearSelectedIds } = useSelectedIds("booking");
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const { disableShiftsDropdown } = systemSettingsDto?.metadata ?? {};
  const resolver = useYupValidationResolver(
    bulkCreateBookingsValidationSchema,
    "booking",
  );
  const selectedBookingCellsIds = useRecoilValue(
    selectedCellsBookingCalendarAtom,
  );
  const bookingDtos = useRecoilValue(selectedCellsBookingsSelector);
  const isBulkBookingSetFinish = useRecoilValue(isBulkBookingSetFinishSelector);
  const isBulkBookingSetAborted = useRecoilValue(
    isBulkBookingSetAbortedSelector,
  );
  const bookingCalendarBulkSetPreviewSuccessfullyBooked = useRecoilValue(
    bookingCalendarBulkSetPreviewSuccessfullyBookedSelector,
  );
  const bulkApprovalBookingConflicts = useRecoilValue(
    bulkApprovalBookingConflictsUpdatedAtom,
  );
  const lastChosenBulkCreateBookingDirection = useRecoilValue(
    lastChosenBulkCreateBookingDirectionAtom,
  );
  const shiftDropdownItems: IDropdownItemShifts[] = useRecoilValue(
    dropdownAtomFamily("shift"),
  );
  const { newBookingsIds, isLoading: isBulkBookingSetProceeding } =
    useRecoilValue(bookingBulkCreateStateFromPollingState);

  const methods = useForm<IBulkSetBookingsForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      direction: disableShiftsDropdown
        ? (lastChosenBulkCreateBookingDirection ?? DirectionEnum.Both)
        : DirectionEnum.Both,
      shouldReplaceExisting: false,
      // RYDE-7139: removed with the "existing ride" conflict option (superseded by shouldReplaceExisting)
      // conflictsDetailsCreateIfExisting: "false",
      conflictsDetailsCreateIfInside: "false",
      conflictsDetailsCreateIfOverlap: "false",
      conflictsDetailsCreateIfGap: false,
      conflictsDetailsCreateIfIneligible: "false",
    },
    resolver,
  });
  const {
    getValues,
    setValue,
    trigger,
    control,
    formState: { isDirty },
  } = methods;
  const directionMultiplier = getBulkBookingDirectionMultiplier({
    direction: getValues()?.direction,
    isMealOnlyShift: isMealOnlyShiftSelected({
      shiftDropdownItems,
      shiftId: getValues()?.shiftId,
    }),
  });
  const bookingCount = selectedBookingCellsIds.size * directionMultiplier;

  const bulkApprovalBookingConflictsApproved = Array.from(
    bulkApprovalBookingConflicts.values(),
  )
    .flat()
    .filter(({ approvalReason }) => !approvalReason);

  const bookingCalendarBulkSetPreviewSuccessfullyBookedUpdated =
    bookingCount - bulkApprovalBookingConflictsApproved.length;

  const getPayload = useCallback(
    ({
      isPreview,
      approvalConflicts,
    }: IUseBulkCreateGetPayloadParams): IBulkSetBookingsDto => {
      const {
        shouldReplaceExisting,
        // conflictsDetailsCreateIfExisting, // RYDE-7139: no longer destructured/sent (superseded by shouldReplaceExisting)
        conflictsDetailsCreateIfInside,
        conflictsDetailsCreateIfOverlap,
        conflictsDetailsCreateIfGap,
        conflictsDetailsCreateIfIneligible,
        ...formValues
      } = getValues();

      const passengersData: IBulkSetBookingsPassengerDto[] = [
        ...selectedBookingCellsIds,
      ].map(bookingCellId => {
        const { passengerId, date } = parseBookingCellId(bookingCellId);

        return {
          passengerId,
          date,
          direction: formValues.direction,
          arrivalTime:
            formValues.direction === DirectionEnum.Both ||
            formValues.direction === DirectionEnum.PickUp
              ? formValues.arrivalTime
              : undefined,
          departureTime:
            formValues.direction === DirectionEnum.Both ||
            formValues.direction === DirectionEnum.DropOff
              ? formValues.departureTime
              : undefined,
          shiftId: formValues.shiftId ?? undefined,
          flightId: formValues.flightId ?? undefined,
          mealId: formValues.mealId ?? undefined,
          originStationIdA: formValues.originStationIdA ?? undefined,
          destinationStationIdA: formValues.destinationStationIdA ?? undefined,
          originStationIdB: formValues.originStationIdB ?? undefined,
          destinationStationIdB: formValues.destinationStationIdB ?? undefined,
          hubIdA: formValues.hubStationA ?? undefined,
          hubIdB: formValues.hubStationB ?? undefined,
        };
      });

      const payload: IBulkSetBookingsDto = {
        passengersData,
        isPreview,
        shouldReplaceExisting,
        conflicts: {
          // createIfExisting: conflictsDetailsCreateIfExisting === "true", // RYDE-7139: superseded by shouldReplaceExisting
          createIfInside: conflictsDetailsCreateIfInside === "true",
          createIfOverlap: conflictsDetailsCreateIfOverlap === "true",
          createIfGap: conflictsDetailsCreateIfGap,
          createIfIneligible: conflictsDetailsCreateIfIneligible === "true",
        },
        approvalBookingConflicts:
          getApprovalBookingConflictAPI(approvalConflicts),
      };

      return payload;
    },
    [getValues, selectedBookingCellsIds],
  );

  const handleCloseAndClearIdsDrawer = useEvent((): void => {
    const isBookingWasCreated = newBookingsIds?.length;
    const isAllBookingWasFailed =
      isPreviewStep && bookingCalendarBulkSetPreviewSuccessfullyBooked <= 0;
    if (isBookingWasCreated || isAllBookingWasFailed) {
      clearSelectedIds();
    }

    onCloseDrawer?.();
  });

  const {
    onCloseClick,
    saveLabel,
    onSaveClick,
    cancelLabel,
    onCancelClick,
    isPreviewStep,
    refreshHandler,
    abortHandler,
    setBulkBookingStateToForm,
    fetchBookings,
    fetchPassengerStationsAndSetValue,
  } = useBulkCreateBooking({
    onCloseDrawer: handleCloseAndClearIdsDrawer,
    getPayload,
    isDirty,
    trigger,
  });

  const shiftIdWatch = useWatch({
    control,
    name: BOOKING_BULK_FORM_FIELDS.shiftId,
  });
  const directionWatch = useWatch({
    control,
    name: BOOKING_BULK_FORM_FIELDS.direction,
  });

  const isProduceButtonActive = Boolean(
    isPreviewStep && newBookingsIds?.length && isBulkBookingSetFinish,
  );

  const onProduce = useEvent(async (): Promise<void> => {
    const [direction, bookingsIds] = getDirectionAndBookingIds(bookingDtos);

    const handleProduceResult = await openBookingProduction({
      bookingsIds,
      direction,
    });

    if (handleProduceResult.success) {
      handleCloseAndClearIdsDrawer();
      setBulkBookingStateToForm();
      fetchBookings();
    }
  });

  useEffect(() => {
    const fetchAndSet = async (): Promise<void> => {
      const result = await fetchPassengerStationsAndSetValue();

      if (result.success) {
        const passengerStations = result.data || [];

        passengerStations.forEach(passengerStationResult => {
          const isPickUp =
            passengerStationResult.direction === DirectionEnum.PickUp;
          const originStationField = isPickUp
            ? BOOKING_BULK_FORM_FIELDS.originStationIdA
            : BOOKING_BULK_FORM_FIELDS.originStationIdB;
          const destinationStationField = isPickUp
            ? BOOKING_BULK_FORM_FIELDS.destinationStationIdA
            : BOOKING_BULK_FORM_FIELDS.destinationStationIdB;

          if (
            activeStationsList.some(
              s => s?.value === passengerStationResult.originStationId,
            )
          ) {
            setValue(
              originStationField,
              passengerStationResult.originStationId,
            );
          }

          if (
            activeStationsList.some(
              s => s?.value === passengerStationResult.destinationStationId,
            )
          ) {
            setValue(
              destinationStationField,
              passengerStationResult.destinationStationId,
            );
          }
        });
      }
    };

    fetchAndSet();
  }, [
    fetchPassengerStationsAndSetValue,
    setValue,
    shiftIdWatch,
    directionWatch,
    activeStationsList,
  ]);

  const isSaveButtonDisabled =
    isBulkBookingSetAborted ||
    (isPreviewStep &&
      (isBulkBookingSetProceeding ||
        bookingCalendarBulkSetPreviewSuccessfullyBooked <= 0));

  return (
    <BaseDrawer onCloseDrawer={onCloseClick} drawerNumber={drawerNumber}>
      <CommonDefaultDrawer
        title={t("booking.bulk.setBookings")}
        onCloseDrawer={onCloseClick}
        Footer={
          <BulkCreateBookingFooter
            saveLabel={saveLabel}
            onSaveClick={onSaveClick}
            cancelLabel={cancelLabel}
            onCancelClick={onCancelClick}
            isSaveDisabled={isSaveButtonDisabled}
            isLoading={isBulkBookingSetProceeding}
            isProduceButtonActive={isProduceButtonActive}
            onProduce={onProduce}
          />
        }
      >
        <PageContextWrapper entity="booking">
          <FormProvider {...methods}>
            {isPreviewStep ? (
              <PreviewStepForm
                onSaveClick={onSaveClick}
                bookingCount={bookingCount}
                onRefresh={refreshHandler}
                onAbort={abortHandler}
                successfullyBooked={
                  bookingCalendarBulkSetPreviewSuccessfullyBookedUpdated
                }
              />
            ) : (
              <BulkCreateBookingsForm />
            )}
          </FormProvider>
        </PageContextWrapper>
      </CommonDefaultDrawer>
    </BaseDrawer>
  );
};

export const BulkCreateBookings: FC<
  IBulkCreateBookingsProps & IInjectedDrawerProps
> = props => (
  <Suspense>
    <BulkCreateBookingsSuspended {...props} />
  </Suspense>
);
